import * as THREE from "three";
import { FLUME_WALL_OUT, FLUME_WALL_TOP, flumeDrained, type Scenery } from "../../prims/scenery";
import { toPoints } from "./coaster";
import { samplePath, sideOf, type Kit } from "./kit";

// A log flume (#156) along a path: a wooden trough of running water on
// trestles, with logs floating in it and a splash pool where it ends. The
// path's `y` is the trough's floor, `wide` its width inside (1.6 m). `h` is
// how deep the water runs (0.3 m); under 0.1 it's drained: no water, logs or
// pool, just the walls either side and a floor just under the path, for a
// chute you skate (lay a kicker along it), whose wall tops grind as ledges
// (#170, `flumeLedges` places them from the same sizes).

export function flume(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const wood = s.color ?? "#8d5a3b";
  const HALF = (s.wide ?? 1.6) / 2;
  const depth = s.h ?? 0.3;
  const wet = !flumeDrained(s);
  const samples = samplePath(toPoints(s.path!), 0.8);
  let side: THREE.Vector3 | undefined;
  for (let i = 0; i < samples.length - 1; i++) {
    const a = samples[i]!.at;
    const b = samples[i + 1]!.at;
    const dir = b.clone().sub(a);
    const len = dir.length();
    dir.normalize();
    side = sideOf(dir, side);
    const up = new THREE.Vector3().crossVectors(side, dir).normalize();
    const frame = new THREE.Matrix4().makeBasis(side, up, dir.clone().negate());
    const mid = a.clone().add(b).multiplyScalar(0.5);
    const part = (w: number, h: number, x: number, y: number, mat: THREE.Material) => {
      const m = new THREE.Mesh(kit.geom("box", () => new THREE.BoxGeometry(1, 1, 1)), mat);
      m.scale.set(w, h, len + 0.05);
      m.quaternion.setFromRotationMatrix(frame);
      m.position.copy(mid).addScaledVector(side!, x).addScaledVector(up, y);
      m.castShadow = m.receiveShadow = true;
      root.add(m);
    };
    part(HALF * 2 + 0.3, 0.15, 0, -0.11, kit.mat(wood, "plywood"));
    // The walls: 0.9 m tall, their tops FLUME_WALL_TOP off the floor.
    for (const x of [-HALF - FLUME_WALL_OUT, HALF + FLUME_WALL_OUT]) part(0.15, 0.9, x, FLUME_WALL_TOP - 0.45, kit.mat(wood, "plywood"));
    if (wet) part(HALF * 2, 0.04, 0, depth, kit.glow("#5ec8e5"));
    if (i % 4 === 0 && mid.y > 0.8) {
      for (const x of [-HALF, HALF]) {
        const top = mid.clone().addScaledVector(side, x).addScaledVector(up, -0.15);
        kit.rod(root, new THREE.Vector3(top.x, 0, top.z), top, 0.12, "#6b4429", "plywood");
      }
    }
  }
  if (!wet) return root;
  // Logs riding the water.
  for (const k of [0.2, 0.55, 0.85]) {
    const at = samples[Math.floor(k * (samples.length - 1))]!;
    const log = new THREE.Group();
    kit.box(log, 1.1, 0.5, 2.4, "#a0522d", 0, 0, 0, "plywood");
    kit.box(log, 0.8, 0.3, 0.5, "#6b4429", 0, 0.5, -0.8, "plywood");
    log.position.copy(at.at).y += 0.15;
    log.lookAt(at.at.clone().add(at.dir));
    root.add(log);
  }
  // The splash pool at the end.
  const end = samples[samples.length - 1]!.at;
  kit.cyl(root, 3.2, 3.4, 0.5, "#b9b2a6", end.x, 0, end.z, 24, "concrete");
  const pool = new THREE.Mesh(new THREE.CircleGeometry(2.9, 24), kit.glow("#5ec8e5"));
  pool.rotation.x = -Math.PI / 2;
  pool.position.set(end.x, 0.52, end.z);
  root.add(pool);
  return root;
}
